/**
 * AthleteModeManager — small per-athlete control rendered on the parent
 * dashboard. Shows the current state ("Sign-in OFF" / "Sign-in ON"), and on
 * click opens a modal where the parent can:
 *   - Confirm consent + the athlete's email
 *   - Pick which sections the kid can see (Workouts / Sessions / Maxes /
 *     Book own sessions)
 *   - Send a fresh magic-link sign-in invite
 *   - Revoke access (kills the kid's sessions + clears their permissions)
 */
import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, ShieldCheck, ShieldOff, Mail, X as CloseIcon } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PERMISSION_LIST = [
    { key: "workouts", label: "Workouts", help: "View today's workout + the runner with plate calc + auto-progression." },
    { key: "sessions", label: "Sessions", help: "See sessions left and what's coming up on the schedule." },
    { key: "maxes", label: "Rep maxes", help: "View and update their own rep maxes." },
    { key: "book_own_sessions", label: "Book own sessions", help: "Lets them book + cancel slots against your plan." },
];

const DEFAULT_PERMS = {
    workouts: true,
    sessions: true,
    maxes: true,
    book_own_sessions: false,
};

export default function AthleteModeManager({ athlete, onChange }) {
    const [open, setOpen] = useState(false);
    const mode = athlete.athlete_mode || { enabled: false };
    const isOn = mode.enabled;

    return (
        <>
            <button
                type="button"
                data-testid={`athlete-mode-btn-${athlete.athlete_id}`}
                onClick={() => setOpen(true)}
                className={`mt-4 inline-flex items-center gap-1.5 text-[10px] tracking-[0.25em] uppercase px-2.5 py-1.5 border transition-colors ${
                    isOn
                        ? "border-brand-navy-700 text-brand-navy-200 hover:border-brand-navy-500"
                        : "border-zinc-800 text-zinc-300 hover:border-brand-red hover:text-brand-red"
                }`}
            >
                {isOn ? <ShieldCheck className="w-3 h-3" /> : <ShieldOff className="w-3 h-3" />}
                Athlete Mode {isOn ? "On" : "Off"}
            </button>
            {open && (
                <AthleteModeModal
                    athlete={athlete}
                    onClose={() => setOpen(false)}
                    onDone={async () => {
                        setOpen(false);
                        await onChange?.();
                    }}
                />
            )}
        </>
    );
}

function AthleteModeModal({ athlete, onClose, onDone }) {
    const existing = athlete.athlete_mode || { enabled: false };
    const [email, setEmail] = useState(existing.email || athlete.email || "");
    const [permissions, setPermissions] = useState(existing.permissions || DEFAULT_PERMS);
    const [consent, setConsent] = useState(existing.enabled);
    const [busy, setBusy] = useState(false);

    const toggleKey = (k) => setPermissions((p) => ({ ...p, [k]: !p[k] }));

    const save = async () => {
        if (!consent) { toast.error("Please confirm consent"); return; }
        if (!email.trim()) { toast.error("Athlete email is required"); return; }
        setBusy(true);
        try {
            await axios.post(
                `${API}/profile/athletes/${athlete.athlete_id}/athlete-mode`,
                { athlete_id: athlete.athlete_id, enabled: true, permissions, consent: true, email: email.trim() },
                { withCredentials: true },
            );
            toast.success(existing.enabled ? "Athlete Mode updated" : "Sign-in invite sent");
            await onDone();
        } catch (e) {
            toast.error("Couldn't save", { description: e?.response?.data?.detail });
        } finally {
            setBusy(false);
        }
    };

    const revoke = async () => {
        if (!window.confirm("Turn off Athlete Mode? This signs the kid out and clears their access.")) return;
        setBusy(true);
        try {
            await axios.post(
                `${API}/profile/athletes/${athlete.athlete_id}/athlete-mode`,
                { athlete_id: athlete.athlete_id, enabled: false, consent: true },
                { withCredentials: true },
            );
            toast.success("Athlete Mode disabled");
            await onDone();
        } catch (e) {
            toast.error("Couldn't revoke", { description: e?.response?.data?.detail });
        } finally {
            setBusy(false);
        }
    };

    return (
        <div
            data-testid="athlete-mode-modal"
            className="fixed inset-0 z-[60] bg-black/85 backdrop-blur-sm flex items-start sm:items-center justify-center p-4 overflow-y-auto"
        >
            <div className="bg-zinc-950 border border-zinc-800 rounded-2xl w-full max-w-lg my-8">
                <div className="px-6 py-4 border-b border-zinc-800 flex items-center justify-between">
                    <div>
                        <div className="text-[10px] tracking-[0.3em] uppercase text-brand-red">Athlete Mode</div>
                        <h3 className="text-lg font-semibold text-white mt-0.5">
                            {athlete.first_name} {athlete.last_name}
                        </h3>
                    </div>
                    <button
                        type="button"
                        data-testid="athlete-mode-close"
                        onClick={onClose}
                        className="text-zinc-300 hover:text-white"
                    >
                        <CloseIcon className="w-5 h-5" />
                    </button>
                </div>
                <div className="p-6 space-y-5">
                    <p className="text-xs text-zinc-300 leading-relaxed">
                        Lets your athlete sign in to see their own workouts, sessions, and maxes. Billing,
                        plans, and family info stay on your account.
                    </p>

                    <label className="block">
                        <span className="text-[11px] tracking-[0.2em] uppercase text-zinc-300 flex items-center gap-1.5">
                            <Mail className="w-3 h-3" /> Athlete email
                        </span>
                        <input
                            data-testid="athlete-mode-email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            placeholder="kid@example.com"
                            className="mt-1.5 w-full bg-zinc-900 border border-zinc-700 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-brand-red"
                        />
                        <p className="text-[10px] text-zinc-500 mt-1">
                            Their sign-in link emails here. They never see your billing.
                        </p>
                    </label>

                    <div>
                        <div className="text-[11px] tracking-[0.2em] uppercase text-zinc-300 mb-2">
                            What they can see
                        </div>
                        <div className="space-y-2">
                            {PERMISSION_LIST.map((p) => (
                                <label
                                    key={p.key}
                                    className="flex items-start gap-3 border border-zinc-800 bg-zinc-900/50 rounded-lg px-3 py-2 cursor-pointer hover:border-zinc-700"
                                >
                                    <input
                                        type="checkbox"
                                        data-testid={`athlete-perm-${p.key}`}
                                        checked={!!permissions[p.key]}
                                        onChange={() => toggleKey(p.key)}
                                        className="mt-1"
                                    />
                                    <div className="flex-1">
                                        <div className="text-sm text-white">{p.label}</div>
                                        <div className="text-[11px] text-zinc-400 mt-0.5">{p.help}</div>
                                    </div>
                                </label>
                            ))}
                        </div>
                    </div>

                    <label className="flex items-start gap-2 text-xs text-zinc-300">
                        <input
                            type="checkbox"
                            data-testid="athlete-mode-consent"
                            checked={consent}
                            onChange={(e) => setConsent(e.target.checked)}
                            className="mt-0.5"
                        />
                        <span>
                            As the parent/guardian, I consent to my athlete signing in to view the sections above.
                        </span>
                    </label>

                    <div className="flex flex-wrap justify-between gap-2 pt-3">
                        {existing.enabled && (
                            <button
                                type="button"
                                data-testid="athlete-mode-revoke"
                                onClick={revoke}
                                disabled={busy}
                                className="text-xs text-zinc-300 hover:text-brand-red inline-flex items-center gap-1.5"
                            >
                                <ShieldOff className="w-3.5 h-3.5" /> Turn off Athlete Mode
                            </button>
                        )}
                        <div className="flex gap-2 ml-auto">
                            <button
                                type="button"
                                onClick={onClose}
                                disabled={busy}
                                className="btn-ghost-tactical text-xs"
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                data-testid="athlete-mode-save"
                                onClick={save}
                                disabled={busy}
                                className="btn-tactical text-xs"
                            >
                                {busy && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                                {existing.enabled ? "Update access" : "Enable & send invite"}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
